import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AUTH_TOKEN } from "constants/AuthConstant";
import AuthService from "services/AuthService";
import API from "services/Api";
import { setAccessToken } from "services/tokenStore";
import axios from "axios";

const BASE = process.env.REACT_APP_LIVE_URL;

export const initialState = {
  loading: false,
  message: "",
  showMessage: false,
  redirect: "",
  token: null,          // In-memory only — never seeded from localStorage
  initialized: false,   // True once bootRefresh has resolved (success or failure)
  userDetails: JSON.parse(localStorage.getItem("userDetails")) || null,
};

// Called once on app mount. Tries to exchange the HttpOnly refresh cookie for a
// fresh access token. Sets initialized=true regardless of outcome so ProtectedRoute
// knows whether to render or wait.
export const bootRefresh = createAsyncThunk(
  "auth/bootRefresh",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE}/token/refresh/`,
        {},
        { withCredentials: true }
      );
      const { access } = response.data;
      setAccessToken(access);
      return access;
    } catch (err) {
      return rejectWithValue(null);
    }
  }
);

export const signIn = createAsyncThunk(
  "auth/login",
  async (data, { rejectWithValue }) => {
    const { email, password } = data;
    try {
      const response = await API("user/login/", "POST", { email, password });
      if (response.status === "success") {
        const loginData = response.data;

        const profile = {
          id: loginData.user.id,
          email: loginData.user.email,
          full_name: loginData.user.first_name + " " + loginData.user.last_name,
          role: loginData.user.role,
        };

        let userPermisions = [];
        const userPerm = await API(`user/${loginData.user.id}/permissions/`, "GET");
        if (userPerm.status === "success") {
          userPermisions = userPerm.data.data;
        }

        const { access } = loginData.tokens;
        // Store access token in memory only — refresh token arrives as HttpOnly cookie
        setAccessToken(access);
        localStorage.setItem("userDetails", JSON.stringify(profile));
        localStorage.setItem("userPerm", JSON.stringify(userPermisions));

        return { token: access, profile };
      } else {
        const error_msg = response.data.error;
        return rejectWithValue(`Login failed. ${error_msg}`);
      }
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || "Error");
    }
  }
);

export const signUp = createAsyncThunk(
  "auth/register",
  async (data, { rejectWithValue }) => {
    const { email, password } = data;
    try {
      const response = await AuthService.register({ email, password });
      const token = response.data.token;
      localStorage.setItem(AUTH_TOKEN, token);
      return token;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  }
);

export const signOut = createAsyncThunk("auth/logout", async () => {
  setAccessToken(null);
  localStorage.removeItem("userDetails");
  localStorage.removeItem("userPerm");
  // Ask backend to delete the HttpOnly refresh cookie (and blacklist the token)
  try {
    await axios.post(`${BASE}/user/logout/`, {}, { withCredentials: true });
  } catch {
    // Cookie will expire naturally if the server call fails
  }
});

export const signInWithGoogle = createAsyncThunk(
  "auth/signInWithGoogle",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthService.loginInOAuth();
      const token = response.data.token;
      localStorage.setItem(AUTH_TOKEN, token);
      return token;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  }
);

export const signInWithFacebook = createAsyncThunk(
  "auth/signInWithFacebook",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthService.loginInOAuth();
      const token = response.data.token;
      localStorage.setItem(AUTH_TOKEN, token);
      return token;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  }
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    authenticated: (state, action) => {
      state.loading = false;
      state.redirect = "/";
      state.token = action.payload;
    },
    showAuthMessage: (state, action) => {
      state.message = action.payload;
      state.showMessage = true;
      state.loading = false;
    },
    hideAuthMessage: (state) => {
      state.message = "";
      state.showMessage = false;
    },
    signOutSuccess: (state) => {
      state.loading = false;
      state.token = null;
      state.redirect = "/";
    },
    showLoading: (state) => {
      state.loading = true;
    },
    signInSuccess: (state, action) => {
      state.loading = false;
      state.token = action.payload;
    },
    // Lets Api.js update the Redux token after a silent refresh
    setToken: (state, action) => {
      state.token = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // ── bootRefresh ──────────────────────────────────────────
      .addCase(bootRefresh.fulfilled, (state, action) => {
        state.token = action.payload;
        state.initialized = true;
      })
      .addCase(bootRefresh.rejected, (state) => {
        state.token = null;
        state.initialized = true;
      })
      // ── signIn ───────────────────────────────────────────────
      .addCase(signIn.pending, (state) => {
        state.loading = true;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload.token;
        state.userDetails = action.payload.profile;
        state.initialized = true;
      })
      .addCase(signIn.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      // ── signOut ──────────────────────────────────────────────
      .addCase(signOut.fulfilled, (state) => {
        state.loading = false;
        state.token = null;
        state.redirect = "/";
      })
      .addCase(signOut.rejected, (state) => {
        state.loading = false;
        state.token = null;
        state.redirect = "/";
      })
      // ── signUp ───────────────────────────────────────────────
      .addCase(signUp.pending, (state) => {
        state.loading = true;
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload;
      })
      .addCase(signUp.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      // ── OAuth ────────────────────────────────────────────────
      .addCase(signInWithGoogle.pending, (state) => {
        state.loading = true;
      })
      .addCase(signInWithGoogle.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload;
      })
      .addCase(signInWithGoogle.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(signInWithFacebook.pending, (state) => {
        state.loading = true;
      })
      .addCase(signInWithFacebook.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        state.token = action.payload;
      })
      .addCase(signInWithFacebook.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      });
  },
});

export const {
  authenticated,
  showAuthMessage,
  hideAuthMessage,
  signOutSuccess,
  showLoading,
  signInSuccess,
  setToken,
} = authSlice.actions;

export default authSlice.reducer;
